import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, Phone } from "lucide-react";
import { BRAND } from "../mock";
import { useCart } from "../context/CartContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import Logo from "./Logo";

const Header = () => {
  const { count, setDrawerOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop?q=${encodeURIComponent(search.trim())}`);
  };

  const navLinkClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-semibold tracking-wide uppercase transition-colors ${
      isActive ? "text-[#0a0a0a]" : "text-gray-700 hover:text-[#b91c1c]"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="accent-bar" />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-brand">
            <Logo size={60} />
            <div className="flex flex-col leading-none">
              <span className="font-display text-[13px] font-bold tracking-[0.22em] text-[#0a0a0a] hidden sm:block">
                CERTIFIED
              </span>
              <span className="font-display text-[11px] font-semibold tracking-[0.28em] text-[#0a0a0a] mt-0.5 hidden sm:block">
                WELLNESS
              </span>
            </div>
          </Link>

          {/* MADE IN USA — red text, clean (no border box), positioned between brand and search */}
          <div
            className="hidden md:inline-flex items-center gap-1 shrink-0"
            data-testid="header-made-in-usa"
          >
            <span className="h-1 w-1 rounded-full bg-[#b91c1c]" />
            <span className="text-[8.5px] font-bold tracking-[0.22em] text-[#b91c1c]">MADE IN USA</span>
          </div>

          <form
            onSubmit={onSearch}
            className="hidden md:flex flex-1 max-w-xl items-center relative"
          >
            <Search className="absolute left-3 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search catalog…"
              className="pl-9 h-11 border-gray-300 focus-visible:ring-[#0a0a0a]"
            />
          </form>

          <div className="flex items-center gap-1">
            <a
              href={`tel:${BRAND.phone.replace(/[^0-9]/g, "")}`}
              className="hidden lg:flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-[#0a0a0a]"
            >
              <Phone className="h-4 w-4" />
              <span className="font-semibold">{BRAND.phone}</span>
            </a>
            <Button
              variant="ghost"
              size="icon"
              className="hidden md:inline-flex"
              aria-label="Account"
            >
              <User className="h-5 w-5" />
            </Button>
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Cart"
              className="relative inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-gray-100"
            >
              <ShoppingCart className="h-5 w-5 text-gray-800" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#b91c1c] text-white text-[11px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-gray-100"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <nav className="hidden md:flex items-center justify-center gap-1 border-t border-gray-100 py-1">
          <NavLink to="/" end className={navLinkClass}>Home</NavLink>
          <NavLink to="/shop" end className={navLinkClass}>Catalog</NavLink>
          <NavLink to="/about" className={navLinkClass}>About</NavLink>
          <NavLink to="/faq" className={navLinkClass}>FAQ</NavLink>
          <NavLink to="/contact" className={navLinkClass}>Contact</NavLink>
        </nav>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3">
            <form onSubmit={onSearch} className="relative mb-3">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search catalog…"
                className="pl-9 h-10"
              />
            </form>
            <div className="flex flex-col">
              <NavLink to="/" className={navLinkClass} onClick={() => setMobileOpen(false)}>Home</NavLink>
              <NavLink to="/shop" className={navLinkClass} onClick={() => setMobileOpen(false)}>Catalog</NavLink>
              <NavLink to="/about" className={navLinkClass} onClick={() => setMobileOpen(false)}>About</NavLink>
              <NavLink to="/faq" className={navLinkClass} onClick={() => setMobileOpen(false)}>FAQ</NavLink>
              <NavLink to="/contact" className={navLinkClass} onClick={() => setMobileOpen(false)}>Contact</NavLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
